import { ref, onMounted, onUnmounted } from 'vue';

/**
 * Composable for time entry keyboard shortcuts with typing pause detection
 * Implements smart detection to prevent accidental shortcut triggers during typing
 */
export function useTimeEntryShortcuts(options = {}) {
  const {
    onCreateTimeEntry = () => {},
    onSave = () => {},
    onSaveAndNew = () => {},
    onCancel = () => {},
    tracking = null,
    enableGlobalShortcuts = true,
    isFormValid = () => true,
    // Callback to check form validation before save actions
    isSaveAndNewAllowed = () => true // Callback to determine if Save & New action is permitted (e.g., only for new entries)
  } = options;
  const isListenerActive = ref(false);
  const lastTypedAt = ref(0);
  const lastEscapeAt = ref(0);
  const lastSaveAt = ref(0);
  const lastSaveAndNewAt = ref(0);

  // Constants
  const TYPING_PAUSE_THRESHOLD = 250; // ms
  const ESCAPE_DEBOUNCE_THRESHOLD = 100; // ms - prevent duplicate escape events
  const SAVE_DEBOUNCE_THRESHOLD = 3000; // ms - prevent duplicate save actions
  const SAVE_AND_NEW_DEBOUNCE_THRESHOLD = 3000; // ms - prevent duplicate save and new actions

  // Track instrumentation events
  const trackAction = (action, method, additionalParams = {}) => {
    const params = {
      action,
      method,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      ...additionalParams
    };
    if (tracking && typeof tracking.event === 'function') {
      tracking.event('time_entry_action', action, params);
    }
    return params;
  };

  // Detect if user is actively typing (within 250ms)
  const isActivelyTyping = () => {
    const now = Date.now();
    const timeSinceLastType = now - lastTypedAt.value;
    return timeSinceLastType < TYPING_PAUSE_THRESHOLD;
  }; // Handle typing in text inputs to update lastTypedAt
  const handleTextInput = event => {
    // Ignore modifier keys and shortcut keys - don't count as "typing"
    if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
      return;
    }

    // Ignore specific keys that aren't actual typing
    const ignoredKeys = ['Escape', 'Enter', 'Tab', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12'];
    if (ignoredKeys.includes(event.key)) {
      return;
    }
    if (event.target && (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA' || event.target.isContentEditable)) {
      lastTypedAt.value = Date.now();
    }
  };

  // Keyboard event handler with typing pause detection
  const handleKeyDown = event => {
    // Always allow Escape to work (with debounce to prevent duplicates)
    if (event.key === 'Escape') {
      const now = Date.now();
      const timeSinceLastEscape = now - lastEscapeAt.value;

      // Debounce escape events to prevent duplicate tracking
      if (timeSinceLastEscape < ESCAPE_DEBOUNCE_THRESHOLD) {
        return; // Skip duplicate escape event
      }
      lastEscapeAt.value = now;
      event.preventDefault();
      event.stopPropagation(); // Prevent other listeners from handling this

      trackAction('cancel', 'shortcut', {
        key_combination: 'escape',
        element_type: event.target?.tagName || 'document'
      });
      onCancel();
      return;
    }

    // For global shortcuts, skip if inside input fields (except Escape)
    if (enableGlobalShortcuts && (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA' || event.target.tagName === 'SELECT' || event.target.isContentEditable)) {
      return;
    }

    // For modal shortcuts, check typing pause detection
    if (!enableGlobalShortcuts && isActivelyTyping()) {
      return;
    }

    // Detect platform for proper modifier keys - Modern non-deprecated detection
    const isMac = (() => {
      // Modern approach: Use navigator.userAgentData if available
      if (navigator.userAgentData) {
        return navigator.userAgentData.platform === 'macOS';
      }

      // Fallback: Use userAgent string parsing (more reliable than deprecated navigator.platform)
      return /Mac|iPhone|iPad|iPod/.test(navigator.userAgent);
    })();

    // Alternative: Try both modifiers if the primary one doesn't work
    // This handles edge cases where users might press Ctrl+Cmd together
    const hasMainModifier = isMac ? event.metaKey : event.ctrlKey;
    const hasAltModifier = isMac ? event.ctrlKey : event.metaKey;
    const useMainModifier = hasMainModifier;
    const useFallbackModifier = !hasMainModifier && hasAltModifier;

    // Handle New Entry shortcut - Global only
    // Shift+E (blocked in input fields to avoid conflicts as specified by product owner)
    const newEntryCondition = event.shiftKey && !event.ctrlKey && !event.metaKey && !event.altKey && event.key === 'E';
    if (enableGlobalShortcuts && newEntryCondition) {
      // Block if user is in a text input (as specified by product owner)
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA' || event.target.tagName === 'SELECT' || event.target.isContentEditable || event.target.getAttribute('contenteditable') === 'true') {
        return; // Don't trigger when focused on text input
      }
      event.preventDefault();
      trackAction('new_entry', 'shortcut', {
        key_combination: 'shift+e',
        element_type: event.target?.tagName || 'document'
      });
      onCreateTimeEntry();
      return;
    }

    // Handle Save and New shortcut FIRST (more specific)
    // Ctrl+Shift+Enter / Cmd+Shift+Enter for save and new
    const saveAndNewCondition = isSaveAndNewAllowed() && (useMainModifier || useFallbackModifier) && event.shiftKey && !event.altKey && event.key === 'Enter';
    if (saveAndNewCondition) {
      const now = Date.now();
      const timeSinceLastSaveAndNew = now - lastSaveAndNewAt.value;

      // Debounce save and new to prevent multiple rapid entries
      if (timeSinceLastSaveAndNew < SAVE_AND_NEW_DEBOUNCE_THRESHOLD) {
        return; // Skip duplicate save and new action
      }
      lastSaveAndNewAt.value = now;
      event.preventDefault();
      event.stopPropagation(); // Prevent other listeners from handling this

      // Check form validation before proceeding
      if (!isFormValid()) {
        return; // Block action due to validation failure (no tracking)
      }

      // Pass tracking callback to be called after successful save
      const trackingCallback = () => {
        trackAction('save_and_new', 'shortcut', {
          key_combination: isMac ? 'cmd+shift+enter' : 'ctrl+shift+enter',
          element_type: event.target?.tagName || 'document'
        });
      };
      onSaveAndNew(trackingCallback);
      return; // Early return prevents any other shortcuts from firing
    }

    // Handle Save shortcut
    // Ctrl+Enter / Cmd+Enter
    const saveCondition = (useMainModifier || useFallbackModifier) && !event.shiftKey && !event.altKey && event.key === 'Enter';
    if (saveCondition) {
      const now = Date.now();
      const timeSinceLastSave = now - lastSaveAt.value;

      // Debounce save to prevent multiple rapid entries
      if (timeSinceLastSave < SAVE_DEBOUNCE_THRESHOLD) {
        return; // Skip duplicate save action
      }
      lastSaveAt.value = now;
      event.preventDefault();
      event.stopPropagation(); // Prevent other listeners from handling this

      // Check form validation before proceeding
      if (!isFormValid()) {
        return; // Block action due to validation failure (no tracking)
      }

      // Pass tracking callback to be called after successful save
      const trackingCallback = () => {
        trackAction('save', 'shortcut', {
          key_combination: isMac ? 'cmd+enter' : 'ctrl+enter',
          element_type: event.target?.tagName || 'document'
        });
      };
      onSave(trackingCallback);
      return; // Early return prevents any other shortcuts from firing
    }

    // Handle Enter key on focused buttons (only if not already handled by shortcuts above)
    if (event.key === 'Enter' && event.target.tagName === 'BUTTON') {
      // Plain Enter on a focused button (not one of the handled keyboard shortcuts above)
      // We attempt semantic classification; if we can't classify, we skip tracking to avoid 'unknown' noise.
      const el = event.target;

      // Prefer an explicit data attribute if provided
      const explicit = el.getAttribute('data-time-entry-action');
      const rawText = (el.textContent || '').trim().toLowerCase();
      const aria = (el.getAttribute('aria-label') || '').trim().toLowerCase();
      const buttonText = rawText || aria; // use visible text first, fall back to aria-label

      let action = null;
      if (explicit) {
        action = explicit;
      } else if (buttonText.includes('save') && buttonText.includes('new')) {
        action = 'save_and_new';
      } else if (buttonText.includes('save')) {
        action = 'save';
      } else if (buttonText.includes('cancel') || buttonText.includes('discard') || buttonText.includes('close')) {
        action = 'cancel';
      } else if (buttonText.includes('delete') || buttonText.includes('remove')) {
        action = 'delete';
      } else if (buttonText.includes('create') || buttonText.includes('add')) {
        action = 'save'; // treat create/add as save semantics
      }

      // If still not classified, do NOT track to prevent 'unknown' analytics entries
      if (!action) return;
      trackAction(action, 'tab_focus_enter', {
        key_combination: 'enter',
        element_type: 'button',
        button_text: buttonText,
        explicit: !!explicit
      });
      return; // Let the native button click proceed
    }
  };

  // Setup keyboard listeners
  const setupKeyboardListener = () => {
    if (isListenerActive.value) return;

    // For modal shortcuts (enableGlobalShortcuts = false), use capture phase to take precedence
    // For global shortcuts (enableGlobalShortcuts = true), use bubble phase
    const useCapture = !enableGlobalShortcuts;
    document.addEventListener('keydown', handleKeyDown, {
      passive: false,
      capture: useCapture
    });
    document.addEventListener('keydown', handleTextInput, {
      passive: true
    });
    isListenerActive.value = true;
  };

  // Remove keyboard listeners
  const removeKeyboardListener = () => {
    if (!isListenerActive.value) return;
    const useCapture = !enableGlobalShortcuts;
    document.removeEventListener('keydown', handleKeyDown, {
      capture: useCapture
    });
    document.removeEventListener('keydown', handleTextInput);
    isListenerActive.value = false;
  };

  // Helper function for tracking time entry creation from buttons
  const trackTimeEntryCreation = method => {
    trackAction('new_entry', method || 'button_click');
  };

  // Helper function for tracking button clicks
  const trackButtonClick = action => {
    trackAction(action, 'button_click');
  };

  // Lifecycle hooks
  onMounted(() => {
    setupKeyboardListener();
  });
  onUnmounted(() => {
    removeKeyboardListener();
  });

  // Return reactive state and methods
  return {
    isListenerActive,
    setupKeyboardListener,
    removeKeyboardListener,
    trackTimeEntryCreation,
    trackButtonClick,
    trackAction,
    trackTimeEntryAction: trackAction // Alias for compatibility
  };
}